import axios from 'axios';
import { navigateToLogin } from '../utils/navigationHelper';
import { API_CONFIG, FEATURES, TENANT_SUBDOMAIN } from '../config/appConfig';
import tenantStorage from '../utils/tenantStorage';

// Log configuration in development
if (FEATURES.enableDebug) {
    console.log('🔧 API Client Configuration:', {
        baseURL: API_CONFIG.baseURL,
        timeout: API_CONFIG.timeout,
    });
}

const apiClient = axios.create({
    baseURL: API_CONFIG.baseURL,
    // No global timeout - let each request specify its own timeout
    // This prevents the global timeout from canceling long-running requests
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true, // Enable credentials for CORS
});

// Paths that should not attach Authorization (login / OTP / refresh)
const isPublicAuthPath = (url) => {
    if (!url) return false;
    if (url.includes('/auth/native/login/')) return true;
    if (url.includes('/auth/native/forgot-password/')) return true;
    if (url.endsWith('/auth/native/refresh-token')) return true;
    return false;
};

// Global flag to prevent multiple simultaneous token refresh attempts
let isRefreshing = false;
let refreshSubscribers = [];

// Function to add request to queue while refreshing
const onRefreshed = (token) => {
    refreshSubscribers.map(callback => callback(token));
    refreshSubscribers = [];
};

// Function to add callback to queue
const addRefreshSubscriber = (callback) => {
    refreshSubscribers.push(callback);
};

// Add a request interceptor
apiClient.interceptors.request.use((config) => {
    const accessToken = tenantStorage.getItem('accessToken');
    if (!isPublicAuthPath(config.url) && accessToken) {
        config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    
    // Add tenant header for multi-tenancy
    if (TENANT_SUBDOMAIN) {
        config.headers['X-Tenant'] = TENANT_SUBDOMAIN;
    }
    
    // Log requests in debug mode
    if (FEATURES.enableDebug) {
        console.log('🚀 API Request:', {
            method: config.method.toUpperCase(),
            url: config.url,
            baseURL: config.baseURL
        });
    }
    
    return config;
}, (error) => {
    return Promise.reject(error);
});

// Add a response interceptor
apiClient.interceptors.response.use(
    response => response,
    async error => {
        // Handle canceled requests gracefully
        if (error.code === 'ERR_CANCELED' || error.message === 'canceled') {
            console.log('Request was canceled:', error.config?.url);
            return Promise.reject({ ...error, name: 'AbortError' });
        }
        
        const originalRequest = error.config;
        
        if (
            error.response &&
            error.response.status === 401 &&
            !originalRequest._retry &&
            !isPublicAuthPath(originalRequest.url) &&
            !originalRequest.url.endsWith('/auth/native/change-password') &&
            !originalRequest.url.endsWith('/auth/native/refresh-token')
        ) {
            if (isRefreshing) {
                // If already refreshing, queue this request
                return new Promise((resolve, reject) => {
                    addRefreshSubscriber((token) => {
                        if (token) {
                            originalRequest.headers['Authorization'] = `Bearer ${token}`;
                            resolve(apiClient(originalRequest));
                        } else {
                            reject(error);
                        }
                    });
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;
            
            const refreshToken = tenantStorage.getItem('refreshToken');
            
            if (refreshToken) {
                try {
                    const refreshHeaders = { 'Content-Type': 'application/json' };
                    if (TENANT_SUBDOMAIN) {
                        refreshHeaders['X-Tenant'] = TENANT_SUBDOMAIN;
                    }

                    const response = await axios.post(
                        `${API_CONFIG.baseURL}/auth/native/refresh-token`,
                        { refresh_token: refreshToken },
                        {
                            headers: refreshHeaders,
                            timeout: 30000,
                        }
                    );
                    
                    // Update tokens
                    const newAccessToken = response.data.access_token;
                    tenantStorage.setItem('accessToken', newAccessToken);
                    if (response.data.refresh_token) {
                        tenantStorage.setItem('refreshToken', response.data.refresh_token);
                    }
                    
                    // Reset refreshing flag and notify queued requests
                    isRefreshing = false;
                    onRefreshed(newAccessToken);
                    
                    // Retry the original request with new token
                    originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
                    return apiClient(originalRequest);
                } catch (refreshError) {
                    // Handle refresh failure - clear storage and redirect to login
                    console.warn('Token refresh failed:', refreshError.response?.status);
                    isRefreshing = false;
                    onRefreshed(null); // Notify queued requests of failure
                    tenantStorage.clear();
                    if (typeof window !== 'undefined') {
                        window.dispatchEvent(new CustomEvent('renttick-auth-updated'));
                    }
                    navigateToLogin('SESSION_EXPIRED');
                    return Promise.reject(refreshError);
                }
            } else {
                // No refresh token available
                isRefreshing = false;
                tenantStorage.clear();
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('renttick-auth-updated'));
                }
                navigateToLogin('SESSION_EXPIRED');
                return Promise.reject(error);
            }
        }
        
        // Log other types of errors for debugging
        if (FEATURES.enableDebug) {
            console.log('❌ API Error:', {
                url: error.config?.url,
                method: error.config?.method?.toUpperCase(),
                status: error.response?.status,
                message: error.message,
                code: error.code,
                timeout: error.code === 'ECONNABORTED' ? 'Request timed out' : null
            });
        }
        
        return Promise.reject(error);
    }
);

// Generic API functions with default timeout
export const apiGet = (url, config = {}) => {
    const finalConfig = {
        timeout: API_CONFIG.timeout,
        ...config
    };
    
    // Log timeout for debugging
    if (FEATURES.enableDebug || config.timeout > 120000) {
        console.log(`API GET ${url} - Timeout: ${finalConfig.timeout}ms`);
    }
    
    return apiClient.get(url, finalConfig);
};

export const apiPost = (url, data, config = {}) => {
    const finalConfig = {
        timeout: API_CONFIG.timeout,
        ...config
    };
    
    // Log timeout for debugging long-running requests
    if (FEATURES.enableDebug || config.timeout > 120000) {
        console.log(`API POST ${url} - Timeout: ${finalConfig.timeout}ms (${finalConfig.timeout / 1000}s)`);
    }
    
    return apiClient.post(url, data, finalConfig);
};

export const apiDelete = (url, config = {}) => {
    const finalConfig = {
        timeout: API_CONFIG.timeout,
        ...config
    };
    return apiClient.delete(url, finalConfig);
};

export const apiPut = (url, data, config = {}) => {
    const finalConfig = {
        timeout: API_CONFIG.timeout,
        ...config
    };
    return apiClient.put(url, data, finalConfig);
};

export default apiClient;